import {
  AppShell,
  Button,
  Group,
  Modal,
  Skeleton,
  Table,
  Text,
  Loader,
  Paper,
  Checkbox,
  Anchor,
  Box,
  ScrollArea,
  Stack,
} from "@mantine/core";
import {
  DeleteGuestRequest,
  GetGuestsResponseRecord,
  useDeleteGuestMutation,
  useGetGuestsQuery,
} from "../slices/api/guestsApi";
import { JSX, useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { Link, useLocation, useSearch } from "wouter";
import CreateGuest from "./CreateGuest";
import dayjs from "dayjs";
import {
  formatFullName,
  formatPrimaryName,
  getActivityAssignmentsText,
  stringCompare,
} from "../shared/utility";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { formatGuestStatus, GuestStatus } from "../slices/types";
import { TextSearchBox } from "../shared/components/TextSearchBox";
import { useGetVentQuery } from "../slices/api/ventsApi";
import { openConfirmationModal } from "../shared/components/Notifications";
import { useGetAssignmentsQuery } from "../slices/api/assignmentsApi";
import { initialize, updateField } from "../slices/form/guestsTab";
import { RootState } from "../store";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useDownloadGuestsMutation } from "../slices/api/downloadsApi";
import {
  IconAddNew,
  IconCopy,
  IconDelete,
  IconDownload,
  IconEmail,
  IconRefresh,
  IconSendMessage,
  IconUpload,
} from "../shared/components/Icons";
import SendEmail from "./EmailGuests";
import UploadGuests from "./UploadGuests";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { setConsoleIds } from "./Console";
import { TabHeader } from "../shared/components/TabHeader";
import SendGuestMessage from "./SendGuestMessage";
import { LinkAnchorToGuest } from "../shared/components/LinkAnchors";

interface Props {
  ventId: string;
  setEventTabDescription: (name: string) => void;
}
interface Guester {
  status: GuestStatus;
  primaryGuestId?: string;
  activityAssignmentsText: string;
}
type GuestCount = {
  a: number;
  d: number;
  u: number;
  primary: number;
  plusOne: number;
  hasActivities: number;
  hasNoActivities: number;
};

function countGuestStatus(guesters: Guester[] | undefined): GuestCount {
  const guestCount: GuestCount = {
    a: 0,
    d: 0,
    u: 0,
    primary: 0,
    plusOne: 0,
    hasNoActivities: 0,
    hasActivities: 0,
  };
  for (const guester of guesters ?? []) {
    guestCount[guester.status] += 1;
    if (guester.primaryGuestId != null && guester.primaryGuestId.length > 0)
      guestCount.plusOne += 1;
    else guestCount.primary += 1;
    if (
      guester.activityAssignmentsText != null &&
      guester.activityAssignmentsText.length > 0
    )
      guestCount.hasActivities += 1;
    else guestCount.hasNoActivities += 1;
  }
  return guestCount;
}

function GuestsTab(props: Props) {
  const { data } = useAppSelector((state: RootState) => state.guestsTab);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) return; // prevent reset when re-mounting component
    dispatch(
      initialize({
        includeAccepted: true,
        includeDeclined: true,
        includeUndecided: true,
        includePlusOnes: true,
        includePrimary: true,
        includeHasActivities: true,
        includeHasNoActivities: true,
        selectedGuests: {},
        searchText: "",
      })
    );
  }, []);
  const ventId = props.ventId;
  const [location, navigate] = useLocation();
  const search = useSearch();

  const [
    openedSendGuestMessage,
    { open: openSendGuestMessage, close: closeSendGuestMessage },
  ] = useDisclosure(false);

  const [sendMessageGuestId, setSendMessageGuestId] = useState<string>("");

  useEffect(() => {
    // Allows navigation from elsewhere, like from an email link
    const queryParams = new URLSearchParams(search);
    const messageGuestId = queryParams.get("messageguestid") ?? "";
    if (messageGuestId !== "") {
      setSendMessageGuestId(messageGuestId);
      queryParams.delete("messageguestid");
      navigate(`${location}?${queryParams.toString()}`, {
        replace: true,
      });
      openSendGuestMessage();
    }
  }, [search, location]);

  const [openedAddGuest, { open: openAddGuest, close: closeAddGuest }] =
    useDisclosure(false);

  const [openedSendEmail, { open: openSendEmail, close: closeSendEmail }] =
    useDisclosure(false);
  const [
    openedUploadGuests,
    { open: openUploadGuests, close: closeUploadGuests },
  ] = useDisclosure(false);

  const [deleteGuest] = useDeleteGuestMutation();
  const {
    data: getGuestsResponse,
    isFetching,
    isSuccess,
    isError: isErrorFetchingGuests,
    error: guestsError,
    refetch: refetchGuests,
  } = useGetGuestsQuery({
    ventId: ventId,
  });
  const { data: ventResponse } = useGetVentQuery({
    id: ventId,
  });
  const { data: assignmentsResponse } = useGetAssignmentsQuery({
    ventId,
  });

  useEffect(() => {
    if (ventResponse) {
      props.setEventTabDescription(ventResponse.name);
    }
  }, [ventResponse]);

  interface ExtendedGuest extends GetGuestsResponseRecord {
    activityAssignmentsText: string;
  }

  const allGuests = useMemo((): ExtendedGuest[] => {
    if (
      getGuestsResponse?.guests == null ||
      assignmentsResponse == null ||
      ventResponse == null
    )
      return [];
    const extendedGuests = getGuestsResponse.guests.map((guest) => {
      const assignmentText = getActivityAssignmentsText(
        assignmentsResponse.assignments,
        guest.id,
        ventResponse.activities
      );
      const extendedGuest: ExtendedGuest = {
        ...guest,
        activityAssignmentsText: assignmentText,
      };
      return extendedGuest;
    });

    return extendedGuests;
  }, [getGuestsResponse, assignmentsResponse, ventResponse]);

  const searchText = (data?.searchText ?? "").toLowerCase();

  const displayedGuests = useMemo((): ExtendedGuest[] => {
    if (allGuests == null || data == null) return [];
    const filteredGuests = allGuests.filter((guest) => {
      if (guest.firstName === "(private)") return false; // Skip private guests
      return (
        (formatFullName(guest.firstName, guest.lastName)
          .toLowerCase()
          .includes(searchText) ||
          (guest.email ?? "").toLowerCase().includes(searchText) ||
          formatFullName(guest.primaryFirstName, guest.primaryLastName)
            .toLowerCase()
            .includes(searchText)) &&
        ((data.includeUndecided && guest.status === "u") ||
          (data.includeAccepted && guest.status === "a") ||
          (data.includeDeclined && guest.status === "d")) &&
        ((data.includePrimary && guest.primaryGuestId === "") ||
          (data.includePlusOnes && guest.primaryGuestId !== "")) &&
        ((data.includeHasActivities && guest.activityAssignmentsText !== "") ||
          (data.includeHasNoActivities && guest.activityAssignmentsText === ""))
      );
    });
    const sorted = filteredGuests.sort((a, b) => {
      return stringCompare(sortKey(a), sortKey(b));
    });
    return sorted;
  }, [
    allGuests,
    data?.includeAccepted,
    data?.includeDeclined,
    data?.includeHasNoActivities,
    data?.includeHasActivities,
    data?.includePlusOnes,
    data?.includePrimary,
    data?.includeUndecided,
    data?.searchText,
  ]);

  const selectedGuestCount = Object.keys(data?.selectedGuests ?? []).length;

  useEffect(() => {
    // Remove selected guests not displayed
    if (data == null || displayedGuests == null) return;
    // No need to remove if no guests selected
    if (selectedGuestCount === 0) return;
    const newSet: Record<string, boolean> = {};
    for (const extendedGuest of displayedGuests) {
      if (data.selectedGuests[extendedGuest.id])
        newSet[extendedGuest.id] = true;
    }
    dispatch(
      updateField({
        field: "selectedGuests",
        value: newSet,
      })
    );
  }, [displayedGuests]);

  const guestCountAll = useMemo((): GuestCount => {
    return countGuestStatus(allGuests);
  }, [allGuests]);

  const allGuestsMap: Record<string, ExtendedGuest> = useMemo(() => {
    const map: Record<string, ExtendedGuest> = {};
    allGuests.forEach((guest) => {
      map[guest.id] = guest;
    });
    return map;
  }, [allGuests]);

  const [downloadCsv, { isLoading: isLoadingDownloadCsv }] =
    useDownloadGuestsMutation();

  const handleDownload = async () => {
    let objectUrl: string | null = null; // Variable to hold the object URL for cleanup
    try {
      if (data == null) {
        displayErrorMessage("data is null");
        return;
      }
      await downloadCsv({
        ventId: props.ventId,
        guestIds: Object.keys(data.selectedGuests),
      }).unwrap();
    } catch (error) {
      console.log(error);
      displayErrorMessage("Download failed");
      return;
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        console.log("Revoked Object URL:", objectUrl); // For debugging
      }
    }
  };

  const handleDelete = async (guestId: string, ventId: string) => {
    if (guestId === undefined) {
      console.error("guestId is undefined but should not be");
      return;
    }

    const request: DeleteGuestRequest = {
      id: guestId,
      ventId,
    };

    try {
      await deleteGuest(request).unwrap();
      setConsoleIds({ guestId: null });
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const rows = useMemo((): JSX.Element[] => {
    let rows: JSX.Element[] = [];
    if (
      isSuccess &&
      getGuestsResponse?.guests?.length > 0 &&
      data &&
      ventResponse
    ) {
      rows = displayedGuests.map((guest) => {
        let cf_columns: JSX.Element[] = [];
        if (
          ventResponse.canUserViewConfig &&
          ventResponse.customFields &&
          ventResponse.customFields.length > 0
        ) {
          cf_columns = ventResponse.customFields.map((customField) => {
            const values = guest.customFieldValues
              ? guest.customFieldValues[customField.id]
              : [""];
            const value = (values ?? [""]).join(", ");
            return (
              <Table.Td key={customField.id}>
                <EbTooltip label={value}>
                  <Box
                    style={{
                      maxWidth: 100,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {value}
                  </Box>
                </EbTooltip>
              </Table.Td>
            );
          });
        }
        return (
          <Table.Tr key={guest.id}>
            {ventResponse.canUserViewConfig && (
              <Table.Td>
                <Checkbox
                  aria-label="Select guest"
                  checked={data.selectedGuests[guest.id] ?? false}
                  onChange={(event) => {
                    const newSet: Record<string, boolean> = {
                      ...data.selectedGuests,
                    };
                    if (event.currentTarget.checked) {
                      newSet[guest.id] = true; // add
                    } else {
                      delete newSet[guest.id];
                    }
                    dispatch(
                      updateField({
                        field: "selectedGuests",
                        value: newSet,
                      })
                    );
                  }}
                />
              </Table.Td>
            )}
            <Table.Td>
              <Group gap="0.5rem">
                {/* Hosts can always message guests with email addresses */}
                {(ventResponse.allowGuestMessaging ||
                  ventResponse.canUserViewConfig) &&
                  guest.canUserSendMessage && (
                    <EbActionIcon
                      aria-label="Send message to guest"
                      onClick={() => {
                        setSendMessageGuestId(guest.id);
                        openSendGuestMessage();
                      }}
                    >
                      <EbTooltip label="Send message to guest">
                        <IconSendMessage />
                      </EbTooltip>
                    </EbActionIcon>
                  )}
                {guest.canUserDelete && (
                  <EbActionIcon
                    aria-label="Delete guest"
                    onClick={() => {
                      openConfirmationModal({
                        message: `Please confirm deletion of the guest ${formatFullName(guest.firstName, guest.lastName)}`,
                        title: "Delete Guest",
                        onConfirm: () => handleDelete(guest.id, guest.ventId),
                      });
                    }}
                  >
                    <EbTooltip label="Delete guest">
                      <IconDelete />
                    </EbTooltip>
                  </EbActionIcon>
                )}
              </Group>
            </Table.Td>
            <Table.Td>{formatGuestStatus(guest.status)}</Table.Td>
            <Table.Td>
              {(ventResponse.canUserViewConfig || guest.canUserEdit) && (
                <LinkAnchorToGuest ventId={guest.ventId} guestId={guest.id}>
                  {guest.firstName}
                </LinkAnchorToGuest>
              )}
              {!(ventResponse.canUserViewConfig || guest.canUserEdit) && (
                <>{guest.firstName}</>
              )}
            </Table.Td>
            <Table.Td>{guest.lastName}</Table.Td>
            {ventResponse.canUserViewConfig && (
              <Table.Td>
                <EbTooltip label={guest.email}>
                  <Box
                    style={{
                      maxWidth: 100,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {guest.email}
                  </Box>
                </EbTooltip>
              </Table.Td>
            )}
            <Table.Td>
              <div hidden={guest.primaryGuestId == null}>
                {(ventResponse.canUserViewConfig || guest.canUserEdit) && (
                  <Anchor
                    component={Link}
                    href={`/console/guest/event/${ventId}/guest/${guest.primaryGuestId}`}
                  >
                    {formatPrimaryName(
                      guest.primaryGuestId,
                      guest.primaryFirstName,
                      guest.primaryLastName
                    )}
                  </Anchor>
                )}
                {!(ventResponse.canUserViewConfig || guest.canUserEdit) && (
                  <Text size="sm">
                    {formatPrimaryName(
                      guest.primaryGuestId,
                      guest.primaryFirstName,
                      guest.primaryLastName
                    )}
                  </Text>
                )}
              </div>
            </Table.Td>
            <Table.Td
              style={{
                maxWidth: 125,
                whiteSpace: "normal", // Allow text to wrap
                wordBreak: "break-word", // Break long words if necessary
              }}
            >
              {guest.activityAssignmentsText}
            </Table.Td>
            {ventResponse.canUserViewConfig && (
              <Table.Td>
                <EbTooltip label={guest.messages}>
                  <Box
                    style={{
                      maxWidth: 50,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {guest.messages}
                  </Box>
                </EbTooltip>
              </Table.Td>
            )}
            {ventResponse.canUserViewConfig && (
              <Table.Td>
                <EbTooltip label={guest.hostNote}>
                  <Box
                    style={{
                      maxWidth: 50,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {guest.hostNote}
                  </Box>
                </EbTooltip>
              </Table.Td>
            )}
            {cf_columns}
            <Table.Td>
              <EbTooltip label={dayjs(guest.updateTime).format("LLLL z")}>
                <Box
                  style={{
                    maxWidth: 75,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dayjs(guest.updateTime).format("MMM D, YYYY h:mm A z")}
                </Box>
              </EbTooltip>
            </Table.Td>
          </Table.Tr>
        );
      });
    }
    return rows;
  }, [getGuestsResponse, ventResponse, data]);

  const cf_column_headers = useMemo((): JSX.Element[] => {
    let cf_column_headers: JSX.Element[] = [];
    if (
      ventResponse &&
      ventResponse.canUserViewConfig &&
      ventResponse.customFields &&
      ventResponse.customFields.length > 0
    ) {
      cf_column_headers = ventResponse.customFields.map((customField) => {
        return (
          <Table.Th key={customField.id}>
            <EbTooltip label={customField.name}>
              <Box
                style={{
                  maxWidth: 100,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {customField.name}
              </Box>
            </EbTooltip>
          </Table.Th>
        );
      });
    }
    return cf_column_headers;
  }, [ventResponse]);

  if (isErrorFetchingGuests) {
    handleFetchingError(guestsError);
    return null;
  }

  if (!getGuestsResponse || !ventResponse || !assignmentsResponse || !data) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  const selectedGuestIdsWithEmails = Object.keys(data.selectedGuests).filter(
    (guestId) => allGuestsMap[guestId] && allGuestsMap[guestId].email !== ""
  );

  const selectedGuestCountWithEmails = selectedGuestIdsWithEmails.length;

  return (
    <AppShell.Main>
      <Modal
        opened={openedAddGuest}
        onClose={closeAddGuest}
        withCloseButton={false}
        centered
      >
        <CreateGuest
          title="Add Guest"
          onClose={closeAddGuest}
          ventId={ventId}
          emailRequired
          defaultStatus={"u"}
        />
      </Modal>
      <Modal
        opened={openedSendEmail}
        onClose={closeSendEmail}
        withCloseButton={false}
        centered
      >
        <SendEmail
          onClose={closeSendEmail}
          ventId={ventId}
          guestIds={selectedGuestIdsWithEmails}
        />
      </Modal>
      <Modal
        opened={openedUploadGuests}
        onClose={closeUploadGuests}
        withCloseButton={false}
        centered
      >
        <UploadGuests
          onClose={closeUploadGuests}
          ventId={ventId}
          ventName={ventResponse.name}
        />
      </Modal>
      <Modal
        opened={openedSendGuestMessage}
        onClose={closeSendGuestMessage}
        withCloseButton={false}
        centered
      >
        <SendGuestMessage
          isSenderHost={ventResponse.canUserViewConfig}
          onClose={closeSendGuestMessage}
          guestId={sendMessageGuestId}
        />
      </Modal>
      <Stack style={{ overflow: "visible" }}>
        <Group>
          <TabHeader
            label="Guests for"
            ventId={ventId}
            ventName={ventResponse.name}
          />
          <EbActionIcon
            aria-label="Refresh guests"
            onClick={() => {
              refetchGuests();
            }}
          >
            <EbTooltip label="Refresh guests">
              <IconRefresh />
            </EbTooltip>
          </EbActionIcon>
        </Group>
        <Group>
          {ventResponse.canUserEdit && (
            <Button
              disabled={!ventResponse.canUserEdit}
              onClick={openAddGuest}
              rightSection={<IconAddNew />}
            >
              Add Guest
            </Button>
          )}
          {ventResponse.canUserViewConfig && (
            <EbTooltip label="Selected plus ones are not included in the count.">
              <Button
                disabled={
                  !ventResponse.canUserViewConfig ||
                  selectedGuestCountWithEmails === 0
                }
                onClick={openSendEmail}
                rightSection={<IconEmail />}
              >
                {`Send Email to ${selectedGuestCountWithEmails} Selected Guests`}
              </Button>
            </EbTooltip>
          )}
          {ventResponse.canUserViewConfig && (
            <EbTooltip label="Useful for sending emails using your own email sender. Selected plus ones are not included in the count.">
              <Button
                disabled={
                  !ventResponse.canUserViewConfig ||
                  selectedGuestCountWithEmails === 0
                }
                onClick={() => {
                  // Copy emails to clipboard
                  const emails = selectedGuestIdsWithEmails
                    .map((guestId) => allGuestsMap[guestId].email)
                    .join(",");
                  navigator.clipboard.writeText(emails);
                }}
                rightSection={<IconCopy />}
              >
                {`Copy Emails of ${selectedGuestCountWithEmails} Selected Guests`}
              </Button>
            </EbTooltip>
          )}
          {ventResponse.canUserEdit && (
            <Button
              disabled={
                !ventResponse.canUserEdit ||
                selectedGuestCount === 0 ||
                isLoadingDownloadCsv
              }
              onClick={handleDownload}
              rightSection={
                isLoadingDownloadCsv ? <Loader size={14} /> : <IconDownload />
              }
            >
              {`Download ${selectedGuestCount} Selected Guests`}
            </Button>
          )}
          {ventResponse.canUserEdit && (
            <Button
              disabled={!ventResponse.canUserEdit}
              onClick={openUploadGuests}
              rightSection={<IconUpload />}
            >
              Upload Guests
            </Button>
          )}
        </Group>
        <TextSearchBox
          textInputProps={{
            placeholder: "Search name, email, and invited by...",
          }}
          setSearchText={(searchText) => {
            dispatch(
              updateField({
                field: "searchText",
                value: searchText,
              })
            );
          }}
          searchText={data.searchText}
        />
        {(ventResponse.canUserViewConfig ||
          (!ventResponse.excludeDeclinedGuests &&
            !ventResponse.excludeUndecidedGuests)) && (
          <Paper>
            <Group mt="sm" gap="xs">
              <Text>{`Viewing: ${displayedGuests.length} of `}</Text>
              <Text
                td="underline"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    updateField({
                      field: "includeAccepted",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeDeclined",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeUndecided",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePrimary",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePlusOnes",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasActivities",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasNoActivities",
                      value: true,
                    })
                  );
                }}
              >{`${allGuests.length}`}</Text>
              <Checkbox
                checked={data.includeAccepted}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "includeAccepted",
                      value: event.currentTarget.checked,
                    })
                  )
                }
              />
              <Text
                onClick={() => {
                  dispatch(
                    updateField({
                      field: "includeAccepted",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeDeclined",
                      value: false,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeUndecided",
                      value: false,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePrimary",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePlusOnes",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasActivities",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasNoActivities",
                      value: true,
                    })
                  );
                }}
                size="sm"
                td="underline"
                style={{ cursor: "pointer" }}
              >
                Accepted {guestCountAll.a}
              </Text>
              <Checkbox
                checked={data.includeDeclined}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "includeDeclined",
                      value: event.currentTarget.checked,
                    })
                  )
                }
              />
              <Text
                onClick={() => {
                  dispatch(
                    updateField({
                      field: "includeAccepted",
                      value: false,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeDeclined",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeUndecided",
                      value: false,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePrimary",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePlusOnes",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasActivities",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasNoActivities",
                      value: true,
                    })
                  );
                }}
                size="sm"
                td="underline"
                style={{ cursor: "pointer" }}
              >
                Declined {guestCountAll.d}
              </Text>
              <Checkbox
                checked={data.includeUndecided}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "includeUndecided",
                      value: event.currentTarget.checked,
                    })
                  )
                }
              />
              <Text
                onClick={() => {
                  dispatch(
                    updateField({
                      field: "includeAccepted",
                      value: false,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeDeclined",
                      value: false,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeUndecided",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePrimary",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includePlusOnes",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasActivities",
                      value: true,
                    })
                  );
                  dispatch(
                    updateField({
                      field: "includeHasNoActivities",
                      value: true,
                    })
                  );
                }}
                size="sm"
                td="underline"
                style={{ cursor: "pointer" }}
              >
                Undecided {guestCountAll.u}
              </Text>
              {ventResponse.canUserViewConfig && (
                <>
                  <Checkbox
                    checked={data.includePrimary}
                    onChange={(event) =>
                      dispatch(
                        updateField({
                          field: "includePrimary",
                          value: event.currentTarget.checked,
                        })
                      )
                    }
                  />
                  <Text
                    td="underline"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      dispatch(
                        updateField({
                          field: "includeAccepted",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeDeclined",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeUndecided",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePrimary",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePlusOnes",
                          value: false,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasActivities",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasNoActivities",
                          value: true,
                        })
                      );
                    }}
                  >{`Primary ${guestCountAll.primary}`}</Text>
                </>
              )}
              {ventResponse.canUserViewConfig && (
                <>
                  <Checkbox
                    checked={data.includePlusOnes}
                    onChange={(event) =>
                      dispatch(
                        updateField({
                          field: "includePlusOnes",
                          value: event.currentTarget.checked,
                        })
                      )
                    }
                  />
                  <Text
                    td="underline"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      dispatch(
                        updateField({
                          field: "includeAccepted",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeDeclined",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeUndecided",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePrimary",
                          value: false,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePlusOnes",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasActivities",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasNoActivities",
                          value: true,
                        })
                      );
                    }}
                  >{`Plus Ones ${guestCountAll.plusOne}`}</Text>
                </>
              )}
              {ventResponse.canUserViewConfig && (
                <>
                  <Checkbox
                    checked={data.includeHasActivities}
                    onChange={(event) =>
                      dispatch(
                        updateField({
                          field: "includeHasActivities",
                          value: event.currentTarget.checked,
                        })
                      )
                    }
                  />
                  <Text
                    td="underline"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      dispatch(
                        updateField({
                          field: "includeAccepted",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeDeclined",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeUndecided",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePrimary",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePlusOnes",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasActivities",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasNoActivities",
                          value: false,
                        })
                      );
                    }}
                  >{`Has Activities ${guestCountAll.hasActivities}`}</Text>
                </>
              )}
              {ventResponse.canUserViewConfig && (
                <>
                  <Checkbox
                    checked={data.includeHasNoActivities}
                    onChange={(event) =>
                      dispatch(
                        updateField({
                          field: "includeHasNoActivities",
                          value: event.currentTarget.checked,
                        })
                      )
                    }
                  />
                  <Text
                    td="underline"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      dispatch(
                        updateField({
                          field: "includeAccepted",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeDeclined",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeUndecided",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePrimary",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includePlusOnes",
                          value: true,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasActivities",
                          value: false,
                        })
                      );
                      dispatch(
                        updateField({
                          field: "includeHasNoActivities",
                          value: true,
                        })
                      );
                    }}
                  >{`Has No Activities ${guestCountAll.hasNoActivities}`}</Text>
                </>
              )}
            </Group>
          </Paper>
        )}
      </Stack>
      <Skeleton visible={isFetching}>
        <div hidden={!isSuccess || rows.length === 0}>
          <ScrollArea w="100%" h="100vh">
            <Table stickyHeader stickyHeaderOffset={0} mt="xs">
              <Table.Thead>
                <Table.Tr>
                  {ventResponse.canUserViewConfig && (
                    <Table.Th>
                      <Checkbox
                        aria-label="Select row"
                        checked={
                          Object.keys(data.selectedGuests).length ===
                            displayedGuests.length && displayedGuests.length > 0
                        }
                        onChange={(event) => {
                          const newSet: Record<string, boolean> = {};
                          if (event.currentTarget.checked) {
                            displayedGuests.forEach(
                              (extendedGuest) =>
                                (newSet[extendedGuest.id] = true)
                            );
                          }
                          dispatch(
                            updateField({
                              field: "selectedGuests",
                              value: newSet,
                            })
                          );
                        }}
                      />
                    </Table.Th>
                  )}
                  <Table.Th></Table.Th>
                  <Table.Th>Attending</Table.Th>
                  <Table.Th>First Name</Table.Th>
                  <Table.Th>Last Name</Table.Th>
                  {ventResponse.canUserViewConfig && <Table.Th>Email</Table.Th>}
                  <Table.Th>Invited by</Table.Th>
                  <Table.Th>Activities</Table.Th>
                  {ventResponse.canUserViewConfig && (
                    <Table.Th>Messages</Table.Th>
                  )}
                  {ventResponse.canUserViewConfig && (
                    <Table.Th>Host Note</Table.Th>
                  )}
                  {cf_column_headers}
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </ScrollArea>
        </div>
        <div hidden={isFetching || rows.length > 0}>
          No guests displayed. Change search or add a guest.
        </div>
      </Skeleton>
    </AppShell.Main>
  );
}

function sortKey(g: GetGuestsResponseRecord): string {
  // Allows sort by last name, keeping primary guests together with their plus ones
  // Primaries may be undefined, so replace with empty string
  // Primary first name cannot be undefined if there is a primary guest, so need only check primary first name for empty string
  if ((g.primaryFirstName ?? "") === "") {
    return g.lastName + "|" + g.firstName + "||";
  }
  return (
    (g.primaryLastName ?? "") +
    "|" +
    (g.primaryFirstName ?? "") +
    "|" +
    g.lastName +
    "|" +
    g.firstName
  );
}

export default GuestsTab;
